(() => {
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');
  // Keep in sync with the scale() factor on .umikaze-parallax-image in
  // umikaze-custom.css: it's what creates the buffer this offset moves
  // within, so the safe travel distance is derived from it below rather
  // than using one fixed pixel value for every image.
  const imageScale = 1.32;
  const safetyMargin = 0.94;
  const smoothing = 0.22;
  const settleThreshold = 0.05;
  const items = new Map(); // image -> { current, target }
  let observer;
  let refreshScheduled = false;
  let animating = false;

  // The scaled image overhangs its frame by frameHeight * (scale - 1) / 2
  // on each side. Deriving the max offset per image from its own frame
  // height (instead of one constant for every image) means no image's
  // edge is ever exposed, regardless of how tightly it's cropped.
  const computeTarget = (image) => {
    const frameHeight = image.parentElement?.clientHeight || image.getBoundingClientRect().height;
    const maxOffset = (frameHeight * (imageScale - 1) * 0.5) * safetyMargin;

    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;
    const rect = image.getBoundingClientRect();
    const imageCenter = rect.top + rect.height / 2;
    const progress = Math.max(-1, Math.min(1, (imageCenter - viewportCenter) / viewportHeight));
    return -progress * maxOffset;
  };

  // Ease each image's offset toward its scroll-derived target instead of
  // snapping straight to it. This smooths over choppy/bursty scroll events
  // (trackpad momentum, large wheel deltas) so the motion reads as a
  // continuous glide rather than jumps tied 1:1 to raw scroll position.
  const animate = () => {
    let unsettled = false;

    items.forEach((state, image) => {
      const delta = state.target - state.current;
      if (Math.abs(delta) > settleThreshold) {
        state.current += delta * smoothing;
        unsettled = true;
      } else {
        state.current = state.target;
      }
      image.style.setProperty('--umikaze-parallax-y', `${state.current.toFixed(2)}px`);
    });

    if (unsettled) {
      requestAnimationFrame(animate);
    } else {
      animating = false;
    }
  };

  const startAnimating = () => {
    if (animating) return;
    animating = true;
    requestAnimationFrame(animate);
  };

  const refreshTargets = () => {
    refreshScheduled = false;
    items.forEach((state, image) => {
      state.target = computeTarget(image);
    });
    startAnimating();
  };

  const requestUpdate = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    requestAnimationFrame(refreshTargets);
  };

  const disable = () => {
    observer?.disconnect();
    observer = undefined;
    items.clear();
    document.querySelectorAll('.umikaze-parallax-image').forEach((image) => {
      image.classList.remove('umikaze-parallax-image');
      image.style.removeProperty('--umikaze-parallax-y');
      const frame = image.parentElement;
      frame?.classList.remove('umikaze-parallax-frame');
      frame?.style.removeProperty('border-radius');
    });
  };

  const enable = () => {
    disable();
    if (!desktopQuery.matches || !motionQuery.matches) return;

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!items.has(entry.target)) {
            // Snap to the correct offset immediately on entry so only
            // ongoing scroll motion gets eased, not the image's appearance.
            const target = computeTarget(entry.target);
            items.set(entry.target, { current: target, target });
            entry.target.style.setProperty('--umikaze-parallax-y', `${target.toFixed(2)}px`);
          }
        } else {
          items.delete(entry.target);
        }
      });
    }, { rootMargin: '15% 0px' });

    // care_section and people_section's primary photo (e.g. about-us_02.jpg)
    // isn't an "Image" block — it's the media-with-content section's own
    // built-in media, rendered as .media-block instead of .image-block.
    // feature_links_section's WORKSHOP/ONLINE STORE link icons are
    // .image-block too but shouldn't parallax, so they're filtered out.
    // umikaze-scroll-reveal.js mirrors this selector to skip these images.
    document
      .querySelectorAll(
        'main .image-block img, main [id$="__care_section"] .media-block img, main [id$="__people_section"] .media-block img'
      )
      .forEach((image) => {
        if (image.closest('[id$="__feature_links_section"]')) return;
        image.classList.add('umikaze-parallax-image');
        const frame = image.parentElement;
        if (frame) {
          frame.classList.add('umikaze-parallax-frame');
          // scale() pushes the image's own rounded corners outside the
          // frame, so without this the frame's square edge clips them off.
          // Mirroring the image's actual (admin-configurable) radius here
          // — instead of hardcoding one — keeps the theme's own per-block
          // "Corner radius" setting in control. Reading the four longhand
          // corner properties (rather than the borderRadius shorthand,
          // whose getComputedStyle serialization isn't consistent across
          // engines) and rejoining them is the reliable way to copy it.
          const imageStyle = getComputedStyle(image);
          frame.style.borderRadius = [
            imageStyle.borderTopLeftRadius,
            imageStyle.borderTopRightRadius,
            imageStyle.borderBottomRightRadius,
            imageStyle.borderBottomLeftRadius,
          ].join(' ');
        }
        observer.observe(image);
      });
  };

  // At 990px+ the theme makes .page-wrapper the actual scroll container
  // (html/body get overflow: hidden — see base.css), so window never
  // fires 'scroll' there. Below that, .page-wrapper doesn't scroll and
  // window does. Listening on both covers each range without a media
  // query, since only the element that's actually scrolling ever fires.
  const scrollContainer = document.querySelector('.page-wrapper');

  window.addEventListener('scroll', requestUpdate, { passive: true });
  scrollContainer?.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  desktopQuery.addEventListener('change', enable);
  motionQuery.addEventListener('change', enable);
  document.addEventListener('shopify:section:load', enable);

  enable();
})();
